import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { WordMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await register(form.name, form.email, form.password, form.phone);
      navigate("/dashboard");
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[92vh] flex items-center justify-center px-4 sm:px-6 pt-24 pb-16">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <WordMark className="h-28 w-28" />
        </div>
        <h1 className="font-anton uppercase tracking-tighter text-cream text-3xl sm:text-4xl text-center">Join Elysium</h1>
        <p className="text-smoke text-center mt-2 text-sm">Create your member account.</p>

        <form onSubmit={handleSubmit} className="mt-10 space-y-5 bg-surface border border-line p-6 sm:p-8" data-testid="register-form">
          {error && (
            <p className="text-red-400 text-sm" data-testid="register-error-message">
              {error}
            </p>
          )}
          <div>
            <label className="text-xs uppercase tracking-widest text-smoke">Name</label>
            <Input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="rounded-none bg-void border-line text-cream mt-2 h-12 focus-visible:ring-gold focus-visible:border-gold"
              data-testid="register-name-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-smoke">Email</label>
            <Input
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded-none bg-void border-line text-cream mt-2 h-12 focus-visible:ring-gold focus-visible:border-gold"
              data-testid="register-email-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-smoke">Phone</label>
            <Input
              required
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="rounded-none bg-void border-line text-cream mt-2 h-12 focus-visible:ring-gold focus-visible:border-gold"
              data-testid="register-phone-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-smoke">Password</label>
            <Input
              required
              type="password"
              minLength={6}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="rounded-none bg-void border-line text-cream mt-2 h-12 focus-visible:ring-gold focus-visible:border-gold"
              data-testid="register-password-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="rounded-none bg-gold text-void hover:bg-gold-hover font-bold uppercase tracking-wider w-full h-12"
            data-testid="register-submit-button"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
          </Button>
        </form>
        <p className="text-center text-smoke text-sm mt-6">
          Already a member?{" "}
          <Link to="/login" className="text-gold hover:underline" data-testid="register-login-link">
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
